import { Container, Sprite, type Text, type Texture } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureTriggerContent, FeatureTriggerPort, LocalizationService } from "@slot/game-shell";
import { CandyPanelMotion, type CandyPanelMotionTuning } from "../CandyPanelMotion";
import { CANDY_TEXT, candyLabel, candyValue } from "../candyText";

/** The purple candy card (the art's 758×991 at 760 wide) with its three lines on the card's cream. */
const GEOMETRY = {
  content: { width: 900, height: 1160, y: { desktop: 0, mobile: -150, mobile_landscape: 0 } },
  card: { width: 760, height: 994 },
  title: { y: -170, fontSize: 82, box: { width: 560, height: 110 } },
  amount: { y: 40, fontSize: 250 },
  subtitle: { y: 250, fontSize: 92, box: { width: 560, height: 120 } },
} as const;

/** The free-spins trigger behind `FeatureTriggerPort`: "You have won / 10 / Free spins" on the purple card. */
export class FeatureTriggerView extends Container implements FeatureTriggerPort {
  /** Told as the panel starts to enter (the chef's celebration); the round's presentation sets it. */
  public onEnter: (() => void) | null = null;
  private readonly chrome: ModalChrome;
  private readonly motion: CandyPanelMotion;
  private readonly amountLabel: Text;

  public constructor(input: { card: Texture | null; viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; motion: CandyPanelMotionTuning }) {
    super({ label: "FeatureTriggerPanel" });
    const g = GEOMETRY;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxHeight: 1500 }, mobile: { maxWidth: 1000 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);

    const panel = new Container({ label: "Panel" });
    if (input.card) {
      const card = new Sprite({ texture: input.card, anchor: 0.5, label: "Card" });
      card.setSize(g.card.width, g.card.height);
      panel.addChild(card);
    }
    this.amountLabel = candyValue({ style: CANDY_TEXT.amount, fontSize: g.amount.fontSize, y: g.amount.y, label: "AmountLabel" });
    panel.addChild(
      candyLabel({ localization: input.localization, key: "featuretrigger.title", fallback: "You Have Won", style: CANDY_TEXT.heading, ...g.title, label: "Title" }),
      this.amountLabel,
      candyLabel({ localization: input.localization, key: "featuretrigger.subtitle", fallback: "Free Spins", style: CANDY_TEXT.heading, ...g.subtitle, label: "SubTitle" }),
    );
    this.chrome.content.addChild(panel);
    this.motion = new CandyPanelMotion({ chrome: this.chrome, panel, runner: input.runner, tuning: input.motion });
  }

  public show(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setContent(content: FeatureTriggerContent): void {
    this.amountLabel.text = content.amount ?? "";
  }

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public playEnterAnimation(): Promise<void> {
    this.onEnter?.();
    return this.motion.enter();
  }

  public playExitAnimation(): Promise<void> {
    return this.motion.exit();
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
